import {
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  inquiryApi,
  SupplierInquiry,
  TierPrice,
} from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { inquiryEvents } from './events';
import { setValueAsDecimalExtender } from '../sales/shared';

interface Props {
  inquiry: SupplierInquiry;
  tier: TierPrice;
  currencyCode?: string;
  showPrice: boolean;
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  quantity: number;
  price: string;
}

export function EditInquiryTierModal({
  inquiry,
  tier,
  currencyCode,
  showPrice,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      quantity: tier.quantity,
      price: tier.price?.toString() ?? '',
    },
  });

  useEffect(() => {
    reset({
      quantity: tier.quantity,
      price: tier.price?.toString() ?? '',
    });
  }, [reset, tier]);

  const {
    request: updateTierRequest,
    loading: updating,
    error: updateError,
  } = useApiRequest(inquiryApi.batchUpdateTierPrices);

  const submit = async (data: FormValues) => {
    const price = showPrice
      ? data.price === ''
        ? null
        : parseFloat(data.price)
      : tier.price;

    const response = await updateTierRequest(inquiry.id, [
      {
        id: tier.id,
        quantity: data.quantity,
        price,
      },
    ]);

    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('tiers.edit')}</ModalHeader>
        {updateError && <ErrorDetails error={updateError} />}
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <FormControl isRequired={true} isInvalid={!!errors.quantity}>
              <TexasFormLabel>{t('general.quantity')}</TexasFormLabel>
              <Input
                variant="texas"
                type="number"
                autoFocus={true}
                {...register('quantity', {
                  required: true,
                  min: 1,
                  valueAsNumber: true,
                })}
              />
              <FormErrorMessage>{errors.quantity?.message}</FormErrorMessage>
            </FormControl>
            {showPrice && (
              <FormControl mt={4}>
                <TexasFormLabel>{t('inquiry.price')}</TexasFormLabel>
                <TexasInputWrapper rightAddon={currencyCode}>
                  <Input
                    variant="texas"
                    placeholder="—"
                    {...register('price', {
                      setValueAs: (v) => setValueAsDecimalExtender(v, false),
                    })}
                  />
                </TexasInputWrapper>
              </FormControl>
            )}
          </ModalBody>

          <ModalFooter>
            <SubmitButton loading={updating} disabled={updating}>
              {t('general.update')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
